import type {ToastMessageService} from 'shared/components/kp-toast-messages';
import type {FinishedResponseInteractionService} from 'shared/services';
import {Subkind} from 'shared/constants';
import type {Rec, ReportTemplate, UUID} from 'src/typings';
import type {RecordRow} from 'src/features/record-grid/lib/types';
import type {RecordEditationDataService} from 'src/features/record-editation/services/record-editation.data-service';
import type {ProjectReportOptimisationDataService} from './project-report-optimisation.data-service';
import type ReportServerDataService from 'src/features/report-server/services/report-server.data-service';
import {exists, ignoreUnusedProperties} from 'shared/utils/custom-utils';
import {getFirstFieldByFieldTypeIdRecursive} from 'src/features/record-grid/lib/grid-fields';
import {createSetFieldRequestFromGridFieldValue} from 'src/features/erp-sutor/sutor-utils';
import {FOND_REPORT} from 'src/features/erp-sutor/sutor-fonds';
import type {
    DayOptimisationOverviewResponse,
    GroupedProjectItemsResponse,
    OptimisationSumsResponse,
    OverallOptimisationOverviewResponse
} from './types';

export class ProjectReportOptimisationService {
    public static serviceName = 'projectReportOptimisationService';

    /*@ngInject*/
    constructor(private recordEditationDataService: RecordEditationDataService,
                private toastMessageService: ToastMessageService,
                private finishedResponseInteractionService: FinishedResponseInteractionService,
                private reportServerDataService: ReportServerDataService,
                private projectReportOptimisationDataService: ProjectReportOptimisationDataService) {
    }

    public async useForOptimisationBulk(projectRecord: Rec, businessItemRecord: RecordRow, reportsForOptimisation: RecordRow[]): Promise<UUID[]> {
        const results = await Promise.all(reportsForOptimisation.map((report: RecordRow) => this.useForOptimisation(projectRecord, businessItemRecord, report, false)));
        this.toastMessageService.showSuccess('Výkazy byly použity k optimalizaci!');

        return results;
    }

    // TODO: Remove or check businessItemRecord parameter
    public async useForOptimisation(projectRecord: Rec, businessItemRecord: RecordRow, reportForOptimisation: RecordRow, showToastMessage: boolean = true): Promise<UUID | null> {
        ignoreUnusedProperties(businessItemRecord);

        const dateField = getFirstFieldByFieldTypeIdRecursive(reportForOptimisation, FOND_REPORT.dateFieldId);
        const workerField = getFirstFieldByFieldTypeIdRecursive(reportForOptimisation, FOND_REPORT.workerMainFieldId);

        if (!exists(dateField) || !exists(workerField)) {
            this.toastMessageService.showError('Tento výkaz nemůžete použít k optimalizaci, je totiž nevalidní!');
            return null;
        }

        const recordEditation = await this.recordEditationDataService.createNewEditation({
            subkind: Subkind.SUBKIND_DOCUMENT,
            fond: {id: FOND_REPORT.OPTIMISATION.fond},
            rootFond: {id: FOND_REPORT.OPTIMISATION.fond},
            initialValues: {
                [FOND_REPORT.topProjectFieldId]: {
                    recordId: projectRecord.id
                },
                [FOND_REPORT.dateFieldId]: createSetFieldRequestFromGridFieldValue(dateField, true),
                [FOND_REPORT.workerMainFieldId]: createSetFieldRequestFromGridFieldValue(workerField, true)
            }
        });

        try {
            // TODO: In future, it will be as a draft
            await this.recordEditationDataService.publish(recordEditation.id);

            if (showToastMessage) {
                this.toastMessageService.showSuccess('Výkaz byl použit pro optimalizaci!');
            }

            return reportForOptimisation.id;
        } catch (errorException) {
            if (showToastMessage) {
                this.finishedResponseInteractionService.showFailedResponseInToast(errorException);
            }

            return null;
        }
    }

    public async getReportSums(project: Rec): Promise<OptimisationSumsResponse | null> {
        try {
            return await this.projectReportOptimisationDataService.getReportSums(project);
        } catch (exceptionResponse) {
            this.finishedResponseInteractionService.showFailedResponseInToast(exceptionResponse);
            return null;
        }
    }

    public async getOverallOverview(project: Rec): Promise<OverallOptimisationOverviewResponse | null> {
        try {
            return await this.projectReportOptimisationDataService.getOverallOverview(project);
        } catch (exceptionResponse) {
            this.finishedResponseInteractionService.showFailedResponseInToast(exceptionResponse);
            return null;
        }
    }

    public async getItemOverview(project: Rec, projectItem: RecordRow): Promise<DayOptimisationOverviewResponse | null> {
        try {
            return await this.projectReportOptimisationDataService.getItemOverview(project, projectItem);
        } catch (exceptionResponse) {
            this.finishedResponseInteractionService.showFailedResponseInToast(exceptionResponse);
            return null;
        }
    }

    public async getGroupedProjectItems(project: Rec): Promise<GroupedProjectItemsResponse | null> {
        try {
            return await this.projectReportOptimisationDataService.getGroupedProjectItems(project);
        } catch (exceptionResponse) {
            this.finishedResponseInteractionService.showFailedResponseInToast(exceptionResponse);
            return null;
        }
    }

    public async getReportServerTemplateByName(name: string): Promise<ReportTemplate | null> {
        try {
            return await this.reportServerDataService.getReportTemplateByName(name);
        } catch (exceptionResponse) {
            this.finishedResponseInteractionService.showFailedResponseInToast(exceptionResponse);
            return null;
        }
    }
}
